import React, { useMemo } from 'react';
import { Circle, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import UserAvatar from '../avatars/UserAvatar';

const GROUPS = {
  online: { title: 'Online Now', colors: ['green'], icon: <Circle className="h-4 w-4 text-green-500 fill-green-500" /> },
  recent: { title: 'Recently Active', colors: ['yellow', 'orange'], icon: <Clock className="h-4 w-4 text-yellow-500" /> },
  offline: { title: 'Offline', colors: ['gray'], icon: <Circle className="h-4 w-4 text-gray-500 fill-gray-500" /> },
};

const DOT = {
  green: 'bg-green-500',
  fuchsia: 'bg-fuchsia-500',
  yellow: 'bg-yellow-500',
  orange: 'bg-orange-500',
  gray: 'bg-gray-500',
};

export const ActivityMembersDialog = ({ group, onClose, users, getStatus }) => {
  const spec = group ? GROUPS[group] : null;

  const members = useMemo(() => {
    if (!spec) return [];
    return users
      .map((u) => ({ user: u, status: getStatus(u) }))
      .filter(({ status }) => spec.colors.includes(status.color))
      .sort((a, b) => (a.user.name || '').localeCompare(b.user.name || ''));
  }, [users, spec, getStatus]);

  return (
    <Dialog open={!!group} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-gray-900 border-white/10 text-white max-w-md" data-testid="activity-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {spec?.icon} {spec?.title}
            <span className="text-sm font-normal text-gray-400">({members.length})</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Members in this group right now. Status updates live.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto space-y-1 pr-1" data-testid="activity-members-list">
          {members.length === 0 && (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="activity-members-empty">
              No members in this group right now.
            </p>
          )}
          {members.map(({ user, status }) => (
            <div
              key={user.id}
              className="flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-white/5"
              data-testid={`activity-member-${user.id}`}
            >
              <div className="relative flex-shrink-0">
                <UserAvatar
                  name={user.name}
                  avatarId={user.avatar_id}
                  avatarPhotoUrl={user.avatar_photo_url}
                  size="md"
                />
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-gray-900 ${DOT[status.color]} ${status.color === 'green' ? 'animate-pulse' : ''}`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white truncate">{user.name}</p>
                <p className="text-xs text-gray-400 truncate">{user.email}</p>
              </div>
              <span className={`text-xs flex-shrink-0 ${
                status.color === 'green' ? 'text-green-400'
                  : status.color === 'yellow' ? 'text-yellow-400'
                  : status.color === 'orange' ? 'text-orange-400'
                  : 'text-gray-500'
              }`}>
                {status.text}
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityMembersDialog;
